import { useEffect, useRef } from 'react';
import type { BrickInstance } from '../presentation/types';
import { useScene } from '../scene/SceneContext';
import { usePlay } from '../play/PlayController';
import { startDraft } from '../model/choices';
import './settlement-card.css';

function WinningHand({ label, value }: { label: string; value: string }) {
  const [category, cardsText = '', ...details] = value.split(' · ');
  const playedRanks = new Map(Array.from(details.join(' · ').matchAll(/((?:10|[2-9JQKA])[♠♥♦♣]) plays as (A|K|Q|J|10|[2-9])/g), match => [match[1]!, match[2]!]));
  const rankValue = (rank: string) => ({ A: 14, K: 13, Q: 12, J: 11 }[rank] ?? Number(rank));
  const cards = cardsText.split(', ').map(text => {
    const match = /^((?:10|[2-9JQKA])([♠♥♦♣])) \((board|private)\)$/.exec(text);
    return match ? { text: match[1]!, rank: match[1]!.slice(0, -1), suit: match[2]!, source: match[3]! } : null;
  }).sort((a, b) => rankValue(playedRanks.get(b?.text ?? '') ?? b?.rank ?? '') - rankValue(playedRanks.get(a?.text ?? '') ?? a?.rank ?? ''));
  return <li className="dealer-settlement-hand">
    <div className="dealer-settlement-hand-heading"><strong>{label}</strong><span>{category}</span></div>
    {cards.length > 0 && cards.every(Boolean)
      ? <div className="dealer-settlement-card-row" aria-label={`${label}: cards from highest to lowest`}>
        {cards.map((card, index) => card && <div className={`dealer-settlement-playing-card${card.suit === '♥' || card.suit === '♦' ? ' is-red' : ''}`} key={`${card.text}-${index}`} aria-label={`${card.text}, ${card.source}`}>
          <span className="dealer-settlement-card-face"><strong>{card.rank}</strong><span>{card.suit}</span></span>
          <small>{card.source}</small>
        </div>)}
      </div>
      : <p>{cardsText}</p>}
    {details.length > 0 && <p className="dealer-settlement-hand-detail">{details.join(' · ')}</p>}
  </li>;
}

/** Displays only public facts admitted by the package's filtered observation. */
export function SettlementCard(_props: { inst: BrickInstance; seat: string | null }) {
  const { table } = useScene();
  const play = usePlay();
  const result = useRef<HTMLElement>(null);
  const get = (id: string) => table.counters.find(c => c.id === id);
  const headline = get('settlement:headline');
  useEffect(() => {
    if (!headline) return;
    const reveal = () => result.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
    reveal(); window.addEventListener('resize', reveal);
    return () => window.removeEventListener('resize', reveal);
  }, [headline?.value]);
  if (!headline) return null;
  const awards = table.counters.filter(c => c.id.startsWith('settlement:award:'));
  const hands = table.counters.filter(c => c.id.startsWith('settlement:hand:'));
  const held = get('settlement:held');
  const acknowledge = play?.choices?.find(c => c.action.kind === 'ackSettlement');
  return <section ref={result} className="dealer-settlement" aria-label="Deal result" role="status">
    <span className="dealer-settlement-eyebrow">Hand complete</span>
    <h2>{String(headline.value)}</h2>
    {awards.length > 0 && <ul className="dealer-settlement-awards">{awards.map(row => <li key={row.id}><strong>{row.label}</strong><span>{String(row.value)} chips</span></li>)}</ul>}
    {hands.length > 0 && <div className="dealer-settlement-hands"><h3>Cards that made the hand</h3><ul>{hands.map(row => <WinningHand key={row.id} label={row.label} value={String(row.value)} />)}</ul></div>}
    {held && String(held.value) !== '0' && <p className="dealer-settlement-held">{String(held.value)} chips remain in the pot for the next deal or stage.</p>}
    {hands.length === 0 && awards.length > 0 && <p className="dealer-settlement-note">Cards appear here only when this deal revealed them.</p>}
    <div className="dealer-settlement-footer">
      {acknowledge ? <button type="button" className="button button-primary" disabled={play?.pending || play?.readOnly} onClick={() => play?.submitDraft('settlement:ack', acknowledge, startDraft(acknowledge))}>Acknowledge &amp; continue</button> : <span>Waiting for the table to continue</span>}
    </div>
  </section>;
}
